import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { addDoc, collection, getFirestore } from "@firebase/firestore";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReCAPTCHAComponent from "react-google-recaptcha";

const INITIAL_FORM = { name: "", surname: "", email: "" };

function NewsLetter() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isNameValid, setIsNameValid] = useState(true);
  const [isSurnameValid, setIsSurnameValid] = useState(true);
  const [personalDataCheckbox, setPersonalDataCheckbox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const recaptchaRef = useRef(null);
  const firestore = getFirestore();

  function handleOnChange(evt) {
    const { name, value } = evt.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsEmailValid(emailRegex.test(value));
    } else if (name === "name") {
      setIsNameValid(value.trim() !== "");
    } else if (name === "surname") {
      setIsSurnameValid(value.trim() !== "");
    }
  }

  function handlePersonalDataCheckbox(evt) {
    setPersonalDataCheckbox(evt.target.checked);
  }

  function handleCaptchaChange(value) {
    setIsCaptchaVerified(!!value);
  }

  async function handleOnSubmit(e) {
    e.preventDefault();

    try {
      setLoading(true);
      await addDoc(collection(firestore, "newsletter"), form);
      setLoading(false);
      setForm(INITIAL_FORM);
      setPersonalDataCheckbox(false);
      setIsCaptchaVerified(false);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      toast.success("Grazie per esserti iscritt*!", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        closeButton: false,
        style: {
          background: "#145444",
          color: "white",
          borderRadius: "20px",
          textAlign: "center",
          margin: "10px 10px",
        },
      });
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error("Invio fallito, errore del server!", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
    }
  }

  const isFormValid =
    form.name.trim() &&
    form.surname.trim() &&
    form.email.trim() &&
    personalDataCheckbox &&
    isEmailValid &&
    isNameValid &&
    isSurnameValid &&
    isCaptchaVerified;

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 800,
        mx: "auto",
        p: 4,
        my: { xs: 0, sm: 6 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        "@media (max-width: 800px)": {
          mb: 0,
        },
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontWeight: "bold",
          textAlign: "center",
          color: "text.primary",
        }}
      >
        Iscriviti!
      </Typography>

      <Box
        sx={{
          height: 4,
          bgcolor: "primary.main",
          width: 80,
          mx: "auto",
          my: 1,
        }}
      />

      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          mb: 3,
          color: "text.secondary",
        }}
      >
        Non perdere l&apos;opportunità di iscriverti alla mia newsletter,
        compila il modulo sottostante.
      </Typography>

      <Box component="form" noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nome *"
              name="name"
              value={form.name}
              onChange={handleOnChange}
              variant="outlined"
              disabled={loading}
              error={!isNameValid && form.name === ""}
              helperText={
                !isNameValid && form.name === "" ? "Il nome è obbligatorio" : ""
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Cognome *"
              name="surname"
              value={form.surname}
              onChange={handleOnChange}
              variant="outlined"
              disabled={loading}
              error={!isSurnameValid && form.surname === ""}
              helperText={
                !isSurnameValid && form.surname === ""
                  ? "Il cognome è obbligatorio"
                  : ""
              }
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email *"
              name="email"
              type="email"
              value={form.email}
              onChange={handleOnChange}
              variant="outlined"
              error={!isEmailValid}
              helperText={
                !isEmailValid
                  ? "Inserire un'email valida (es. nome@esempio.com)"
                  : ""
              }
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 },
              justifyContent: "space-between",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={personalDataCheckbox}
                  onChange={handlePersonalDataCheckbox}
                  disabled={loading}
                />
              }
              label={
                <Typography variant="body2">
                  <a
                    href="https://www.iubenda.com/privacy-policy/76810248"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "inherit",
                      textDecoration: "none",
                      transition: "color 0.2s ease, text-decoration 0.2s ease",
                      "&:hover": {
                        color: "#008080",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Dichiaro di aver preso visione dell&apos;informativa sulla
                    privacy
                    <OpenInNewIcon
                      sx={{
                        fontSize: "1rem",
                        ml: 0.5,
                        verticalAlign: "middle",
                        position: "relative",
                        top: -1,
                      }}
                    />
                  </a>
                </Typography>
              }
              sx={{ mt: -1 }}
            />
            <ReCAPTCHAComponent
              sitekey="6Le-yO0qAAAAAD12EGhpEmxn9BnkvpGlqu4zQcU6"
              onChange={handleCaptchaChange}
              ref={recaptchaRef}
              theme="light"
              style={{ margin: "10px 0" }}
            />
          </Grid>

          <Grid item xs={12} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleOnSubmit}
              disabled={!isFormValid || loading}
              size="large"
              endIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "1rem",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: 2,
                },
                transition: "all 0.2s ease",
                minWidth: 200,
              }}
            >
              {loading ? "Invio in corso..." : "Iscriviti"}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}

export default NewsLetter;
